import { useState } from 'react';

import { ChevronsUpDown, Loader2 } from 'lucide-react';

import { useGetGames } from '@/api/getGames';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getGameImage } from '@/hooks/useGameImage';
import { useGameSwitch } from '@/hooks/useGameSandboxSwitch';

interface GameBreadcrumbDropdownProps {
  gameCode: string;
  gameName: string;
}

/**
 * Game Breadcrumb Dropdown Component
 * Displays game selector in the breadcrumb, allowing users to switch games
 */
export const GameBreadcrumbDropdown: React.FC<GameBreadcrumbDropdownProps> = ({
  gameCode,
  gameName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: gamesData, isLoading } = useGetGames();

  const { switchGame } = useGameSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const handleGameChange = (newGameCode: string) => {
    switchGame(gameCode, newGameCode);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isLoading}>
          <img
            className="size-5 rounded-sm object-cover"
            src={getGameImage(gameCode)}
            alt={gameName}
          />
          <span>{gameName}</span>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronsUpDown />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-xs">
        <DropdownMenuRadioGroup
          value={gameCode}
          onValueChange={handleGameChange}
          className="max-h-48 overflow-y-auto"
        >
          {gamesData?.games.map((game) => (
            <DropdownMenuRadioItem key={game.game_code} value={game.game_code}>
              {game.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
